(async () => {
  const { default: PhotoSwipeLightbox } = await import(window.__pswpLightboxUrl);

  function buildVideoHtml(videoUrl, host, width, height) {
    const ratio = height / width;
    const maxW = Math.min(width, 1200);
    const maxH = Math.round(maxW * ratio);

    if (host === 'youtube' || host === 'vimeo') {
      let embedUrl = videoUrl;
      if (host === 'youtube' && videoUrl.includes('watch?v=')) {
        embedUrl = videoUrl.replace('watch?v=', 'embed/');
      }
      if (!embedUrl.includes('autoplay')) {
        embedUrl += (embedUrl.includes('?') ? '&' : '?') + 'autoplay=1';
      }
      return `<div class="pswp-video-container" style="width:${maxW}px;max-width:90vw;">
        <div style="position:relative;padding-bottom:${(ratio * 100).toFixed(2)}%;height:0;overflow:hidden;">
          <iframe src="${embedUrl}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:none;" allow="autoplay;encrypted-media;fullscreen" allowfullscreen></iframe>
        </div>
      </div>`;
    }

    return `<div class="pswp-video-container" style="width:${maxW}px;max-width:90vw;">
      <video src="${videoUrl}" controls autoplay playsinline style="width:100%;height:auto;display:block;border-radius:4px;"></video>
    </div>`;
  }

  function collectItems(container) {
    const items = [];
    container.querySelectorAll('[data-pswp-trigger]').forEach((btn) => {
      const wrap = btn.closest('.gallery-media-wrap');
      const img = wrap ? wrap.querySelector('img') : null;
      const mediaType = btn.dataset.pswpType || 'image';
      const w = parseInt(btn.dataset.pswpWidth, 10) || 1200;
      const h = parseInt(btn.dataset.pswpHeight, 10) || 1200;

      if (mediaType === 'video' || mediaType === 'external_video') {
        const videoUrl = btn.dataset.pswpVideoUrl || '';
        const host = btn.dataset.pswpVideoHost || '';
        items.push({
          html: buildVideoHtml(videoUrl, host, w, h),
          msrc: img ? (img.currentSrc || img.src) : '',
          thumbEl: img,
          triggerEl: btn,
        });
      } else {
        items.push({
          src: btn.dataset.pswpSrc,
          width: w,
          height: h,
          msrc: img ? (img.currentSrc || img.src) : '',
          thumbEl: img,
          triggerEl: btn,
        });
      }
    });
    return items;
  }

  function openLightbox(container, clickedTrigger) {
    const items = collectItems(container);
    const index = items.findIndex((item) => item.triggerEl === clickedTrigger);
    if (index < 0 || !items.length) return;

    // Lightbox style (Theme settings > Product Media): "full" = all controls,
    // "simple" = clean viewer: arrows + close only, desktop click closes,
    // mobile keeps swipe / pinch-zoom / drag-down-to-close.
    const simpleMode = window.__pswpStyle === 'simple';

    // Arrow visibility per device via CSS classes — viewport-based, NOT touch
    // detection (browsers often misreport touch support on desktops).
    const mainClassParts = [];
    if (simpleMode) mainClassParts.push('pswp--simple');
    if (window.__pswpArrowsDesktop === false) mainClassParts.push('pswp--no-arrows-desktop');
    if (window.__pswpArrowsMobile === false) mainClassParts.push('pswp--no-arrows-mobile');

    const commonOptions = {
      mainClass: mainClassParts.join(' '),
      dataSource: items,
      pswpModule: () => import(window.__pswpCoreUrl),
      bgOpacity: 0.92,
      showHideAnimationType: 'zoom',
      closeOnVerticalDrag: true,
      pinchToClose: true,
      initialZoomLevel: 'fit',
      maxZoomLevel: 4,
      arrowKeys: true,
    };

    const styleOptions = simpleMode
      ? {
          zoom: false,
          counter: false,
          imageClickAction: 'close',
          tapAction: 'toggle-controls',
          doubleTapAction: 'zoom',
          secondaryZoomLevel: 2,
          wheelToZoom: false,
        }
      : {
          imageClickAction: 'zoom',
          tapAction: 'zoom',
          doubleTapAction: 'zoom',
          secondaryZoomLevel: 2,
          wheelToZoom: true,
        };

    const lightbox = new PhotoSwipeLightbox(Object.assign({}, commonOptions, styleOptions));

    lightbox.addFilter('thumbEl', (_thumb, itemData) => {
      return itemData.thumbEl || _thumb;
    });

    lightbox.on('close', () => {
      if (document.activeElement) document.activeElement.blur();
    });

    lightbox.on('change', () => {
      const pswp = lightbox.pswp;
      if (!pswp) return;
      pswp.element.querySelectorAll('.pswp-video-container video, .pswp-video-container iframe').forEach((el) => {
        const slide = el.closest('.pswp__item');
        if (slide && slide.getAttribute('aria-hidden') === 'true') {
          if (el.tagName === 'VIDEO') el.pause();
          if (el.tagName === 'IFRAME') el.src = el.src;
        }
      });
    });

    lightbox.on('destroy', () => {
      document.querySelectorAll('.pswp .pswp-video-container video').forEach((v) => v.pause());
      document.querySelectorAll('.pswp .pswp-video-container iframe').forEach((f) => { f.src = ''; });
    });

    lightbox.init();
    lightbox.loadAndOpen(index);
  }

  function bindTrigger(container, btn) {
    if (btn._pswpTriggerBound) return;
    btn._pswpTriggerBound = true;

    const open = (e) => {
      e.preventDefault();
      e.stopPropagation();
      openLightbox(container, btn);
    };

    btn.addEventListener('click', open);

    // Mobile: Splide swallows click after touchend, bind touchend directly on the button
    btn.addEventListener('touchend', (e) => {
      if (btn._touchMoved) return; // ignore swipe
      open(e);
    });
    btn.addEventListener('touchstart', () => { btn._touchMoved = false; }, { passive: true });
    btn.addEventListener('touchmove', () => { btn._touchMoved = true; }, { passive: true });
  }

  /**
   * Whole-image click/tap opens the lightbox (Theme settings > Product Media >
   * "Open lightbox by clicking the image"). Links, video controls and the
   * zoom button itself are left alone; swipes on the slider are ignored.
   */
  function shouldIgnoreWrapTarget(target) {
    return Boolean(
      target.closest('[data-pswp-trigger], a, video, iframe, model-viewer, .shopify-model-viewer-ui, button:not([data-pswp-trigger])')
    );
  }

  function bindWrap(container, wrap) {
    if (wrap._pswpWrapBound) return;
    wrap._pswpWrapBound = true;

    const openFromWrap = (e) => {
      // Simple style: the whole image IS the trigger (magnifier is hidden),
      // so click-to-open stays active regardless of the click-image setting.
      if (window.__pswpClickImage === false && window.__pswpStyle !== 'simple') return;
      if (shouldIgnoreWrapTarget(e.target)) return;
      const btn = wrap.querySelector('[data-pswp-trigger]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      openLightbox(container, btn);
    };

    wrap.addEventListener('click', openFromWrap);

    // Mobile: Splide swallows click after touchend — handle taps directly,
    // but never while swiping (touchmove sets the flag).
    wrap.addEventListener('touchend', (e) => {
      if (wrap._wrapTouchMoved) return;
      openFromWrap(e);
    });
    wrap.addEventListener('touchstart', () => { wrap._wrapTouchMoved = false; }, { passive: true });
    wrap.addEventListener('touchmove', () => { wrap._wrapTouchMoved = true; }, { passive: true });
  }

  function initGallery(container) {
    if (!container || container._pswpBound) return;
    container._pswpBound = true;

    container.querySelectorAll('[data-pswp-trigger]').forEach((btn) => {
      bindTrigger(container, btn);
    });
    container.querySelectorAll('.gallery-media-wrap').forEach((wrap) => {
      bindWrap(container, wrap);
    });
  }

  document.querySelectorAll('.product-gallery-main, .gallery-stacked-images').forEach(initGallery);

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.('.product-gallery-main, .gallery-stacked-images')) {
          initGallery(node);
        }
        node.querySelectorAll?.('.product-gallery-main, .gallery-stacked-images').forEach(initGallery);
        // Also rebind any newly added triggers/wraps inside existing containers
        node.querySelectorAll?.('[data-pswp-trigger]').forEach((btn) => {
          const container = btn.closest('.product-gallery-main, .gallery-stacked-images');
          if (container) bindTrigger(container, btn);
        });
        node.querySelectorAll?.('.gallery-media-wrap').forEach((wrap) => {
          const container = wrap.closest('.product-gallery-main, .gallery-stacked-images');
          if (container) bindWrap(container, wrap);
        });
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
